<div class="min-h-screen bg-gray-50 dark:bg-slate-900">
  <!-- Header -->
  <div class="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-10">
    <div class="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          🚀 Recommandations – Prochains événements
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
          Préparez vos stocks pour chaque événement planifié
        </p>
      </div>
      <div class="flex items-center gap-3">
        <select
          [(ngModel)]="sortBy"
          class="text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800"
          >
          <option value="date">Trier par date</option>
          <option value="name">Trier par nom</option>
        </select>
        <button
          (click)="loadEvents()"
          class="px-3 py-1.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-600 dark:bg-slate-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 transition"
          >
          🔄 Actualiser
        </button>
        <button
          (click)="navigateToCreateEvent()"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
          >
          ➕ Nouvel événement
        </button>
      </div>
    </div>
  </div>

  <div class="max-w-5xl mx-auto px-6 py-6">
    <!-- KPIs -->
    <div class="grid grid-cols-3 gap-4 mb-6">
      <div
        class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-4"
        >
        <div
          class="w-11 h-11 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center text-xl"
          >
          📅
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Événements planifiés</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ eventRows.length }}</p>
        </div>
      </div>
      <div
        class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-4"
        >
        <div
          class="w-11 h-11 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center text-xl"
          >
          📦
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Articles recommandés (chargés)</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ totalItems }}</p>
        </div>
      </div>
      <div
        class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-4"
        >
        <div
          class="w-11 h-11 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center text-xl"
          >
          💰
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">CA estimé total</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ totalEstimatedRevenue }}
          </p>
        </div>
      </div>
    </div>

    <!-- Loading -->
    @if (isLoading) {
      <div class="space-y-3">
        @for (_ of [1, 2, 3]; track _) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 h-24 animate-pulse"
          ></div>
        }
      </div>
    }

    <!-- Error -->
    @if (!isLoading && hasError) {
      <div
        class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center"
        >
        <p class="text-red-700 dark:text-red-300 font-medium mb-2">
          ❌ Impossible de charger les événements
        </p>
        <button (click)="loadEvents()" class="text-sm text-red-600 dark:text-red-400 underline">
          Réessayer
        </button>
      </div>
    }

    <!-- Empty -->
    @if (!isLoading && !hasError && eventRows.length === 0) {
      <div
        class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-10 text-center"
        >
        <p class="text-4xl mb-3">📭</p>
        <p class="text-gray-600 dark:text-gray-400 font-medium mb-1">Aucun événement planifié</p>
        <p class="text-gray-400 text-sm mb-4">
          Créez votre premier événement pour obtenir des recommandations IA
        </p>
        <button
          (click)="navigateToCreateEvent()"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
          >
          ➕ Créer un événement
        </button>
      </div>
    }

    <!-- Event cards -->
    @if (!isLoading && !hasError) {
      <div class="space-y-4">
        @for (row of sortedRows; track row) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden transition-shadow hover:shadow-md dark:shadow-slate-900/50"
            >
            <!-- Card header -->
            <div class="p-5">
              <div class="flex items-start justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    >
                    {{ getEventTypeIcon(row.event.eventType) }}
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                        {{ row.event.name }}
                      </h3>
                      <span
                        class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium"
                        >
                        {{ getEventTypeLabel(row.event.eventType) }}
                      </span>
                      @if (resolveDisplayStatus(row.event) === 'active') {
                        <span
                          class="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium"
                          >
                          🟢 En cours
                        </span>
                      }
                      @if (resolveDisplayStatus(row.event) === 'planned') {
                        <span
                          class="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded text-xs font-medium"
                          >
                          📅 Planifié
                        </span>
                      }
                    </div>
                    <div class="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <span>📅 {{ formatDate(row.event.eventDate) }}</span>
                      @if (row.event.startTime) {
                        <span
                          >⏰ {{ row.event.startTime }}–{{ row.event.endTime }}</span
                          >
                        }
                        <span>📍 {{ getLocationLabel(row.event.location) }}</span>
                      </div>
                      <div class="flex items-center gap-4 text-sm mt-1">
                        @if (row.event.estimatedRevenue) {
                          <span
                            class="text-green-600 dark:text-green-400 font-medium"
                            >
                            💰 {{ row.event.estimatedRevenue.low | number: "1.0-0" }}–{{
                            row.event.estimatedRevenue.high | number: "1.0-0"
                            }}€ estimé
                          </span>
                        }
                        @if (row.event.expectedAttendance) {
                          <span
                            class="text-gray-500 dark:text-gray-400"
                            >
                            👥 Affluence:
                            <strong class="text-gray-700 dark:text-gray-300">{{
                              row.event.expectedAttendance
                            }}</strong>
                          </span>
                        }
                        <!-- Météo forecast badge -->
                        <span
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          [ngClass]="weatherBadgeClass(row)"
                          [title]="weatherLabel(row)"
                          >
                          <span>{{ weatherIcon(row) }}</span>
                          <span>{{ weatherLabel(row) }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <button
                      (click)="navigateToEvent(row.event.id)"
                      class="px-3 py-1.5 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900 rounded-lg text-sm transition"
                      >
                      Détails
                    </button>
                    <button
                      (click)="toggleDetails(row)"
                      class="px-4 py-1.5 rounded-lg text-sm font-medium transition"
                [class]="
                  row.expanded
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 dark:bg-slate-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                "
                      >
                      {{ row.expanded ? "▲ Masquer" : "📦 Recommandations" }}
                    </button>
                  </div>
                </div>
              </div>
              <!-- Recommendations panel -->
              @if (row.expanded) {
                <div
                  class="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-5"
                  >
                  <!-- Loading recos -->
                  @if (row.loading) {
                    <div
                      class="flex items-center gap-3 text-gray-500 dark:text-gray-400"
                      >
                      <div
                        class="w-5 h-5 border-2 border-blue-400 dark:border-blue-700 border-t-transparent rounded-full animate-spin"
                      ></div>
                      <span class="text-sm">Chargement des recommandations IA…</span>
                    </div>
                  }
                  <!-- No recos -->
                  @if (!row.loading && row.recoError) {
                    <div class="text-center py-6">
                      <p class="text-gray-400 text-sm mb-1">
                        ⚠️ Aucune recommandation disponible pour cet événement
                      </p>
                      <p class="text-gray-400 text-xs">
                        Les recommandations sont générées automatiquement après la
                        création de l'événement.
                      </p>
                      <button
                        (click)="loadRecos(row)"
                        class="mt-3 text-xs text-blue-600 dark:text-blue-400 underline"
                        >
                        Régénérer
                      </button>
                    </div>
                  }
                  <!-- Products list -->
                  @if (!row.loading && row.recommandations.length > 0) {
                    <div>
                      <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        @if (resolveDisplayStatus(row.event) === 'active') {
                          🛒 {{ row.recommandations.length }} produit(s) en vente
                        }
                        @if (resolveDisplayStatus(row.event) !== 'active') {
                          📦 {{ row.recommandations.length }} produit(s) à préparer
                        }
                      </p>
                      <div class="space-y-2">
                        @for (rec of row.recommandations; track rec) {
                          <div
                            class="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-4"
                            >
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center justify-between mb-1">
                                <span class="font-semibold text-gray-900 dark:text-white truncate">{{
                                  rec.productName
                                }}</span>
                                <span class="text-xs text-gray-400 ml-2 flex-shrink-0">{{
                                  rec.unit
                                }}</span>
                              </div>
                              <p class="text-xs text-gray-500 dark:text-gray-400 truncate mb-2">
                                {{ rec.explanation }}
                              </p>
                              <div class="flex items-center gap-2">
                                <div
                                  class="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden"
                                  >
                                  <div
                                    class="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
                        [style.width.%]="
                          (rec.recommended /
                            (rec.rangeHigh || rec.recommended + 1)) *
                          100
                        "
                                  ></div>
                                </div>
                                <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                  {{ rec.rangeLow }}–{{ rec.rangeHigh }}
                                </span>
                              </div>
                            </div>
                            <div class="text-center flex-shrink-0 w-20">
                              <p class="text-2xl font-bold text-blue-700 dark:text-blue-300">
                                {{ rec.recommended }}
                              </p>
                              <p class="text-xs text-gray-400">recommandé</p>
                            </div>
                          </div>
                        }
                      </div>
                      <div class="mt-4 flex gap-3">
                        <button
                          (click)="navigateToEvent(row.event.id)"
                          class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
                          >
                          Voir l'événement complet
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
